import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import ConfirmDialog from './ConfirmDialog';

export interface ConfirmRequest {
  title: string;
  /** What happens if the person confirms, in plain words. */
  consequence: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
}

interface PendingConfirm extends ConfirmRequest {
  resolve: (confirmed: boolean) => void;
}

export interface ConfirmController {
  /** Opens the dialog; resolves true on confirm, false on cancel or dismiss. */
  confirm: (request: ConfirmRequest) => Promise<boolean>;
  /** Render this once where the asking component renders. */
  confirmDialog: ReactNode;
}

/**
 * Asks before an action from event handlers and async flows, in place of the
 * browser's own confirm box. A newer request cancels an unanswered one, and
 * an unanswered request resolves false when the component unmounts.
 */
export function useConfirm(): ConfirmController {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const pendingRef = useRef<PendingConfirm | null>(null);

  const settle = useCallback((confirmed: boolean) => {
    const current = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    current?.resolve(confirmed);
  }, []);

  const confirm = useCallback(
    (request: ConfirmRequest) =>
      new Promise<boolean>((resolve) => {
        pendingRef.current?.resolve(false);
        const next = { ...request, resolve };
        pendingRef.current = next;
        setPending(next);
      }),
    [],
  );

  useEffect(
    () => () => {
      pendingRef.current?.resolve(false);
      pendingRef.current = null;
    },
    [],
  );

  const confirmDialog = (
    <ConfirmDialog
      isOpen={pending != null}
      title={pending?.title ?? ''}
      consequence={pending?.consequence ?? null}
      confirmLabel={pending?.confirmLabel ?? ''}
      cancelLabel={pending?.cancelLabel}
      destructive={pending?.destructive}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );

  return { confirm, confirmDialog };
}
